import type { FC } from 'react';

import styles from './Table.module.css';

const TABLE_CELL_TYPE_DATA = 'td';
const TABLE_CELL_TYPE_HEADER = 'th';
const TABLE_CELL_SCOPE_ROW = 'row';
const TABLE_CELL_SCOPE_COL = 'col';

type TableCellType =
  | typeof TABLE_CELL_TYPE_DATA
  | typeof TABLE_CELL_TYPE_HEADER;

type TableCellScope = typeof TABLE_CELL_SCOPE_ROW | typeof TABLE_CELL_SCOPE_COL;

interface TableProps {
  readonly className?: string;
  readonly unstyled?: boolean;
  readonly children: React.ReactNode;
  readonly role?: string;
  [x: string]: unknown;
}

interface TableBodyProps {
  readonly children: React.ReactNode;
  readonly className?: string;
}

interface TableHeadProps {
  readonly children: React.ReactNode;
}

interface TableRowProps {
  readonly className?: string;
  readonly children: React.ReactNode;
  [x: string]: unknown;
}

interface TableCellProps {
  readonly Element?: TableCellType;
  readonly className?: string;
  readonly children: React.ReactNode;
  readonly scope?: TableCellScope;
  readonly colSpan?: number;
  readonly rowSpan?: number;
  readonly other?: unknown;
  readonly width?: string;
}

const Table: FC<TableProps> = ({
  children,
  unstyled = false,
  className = '',
  ...props
}) => (
  <table
    role={props.role}
    className={`${styles['Table']} ${
      !unstyled ? styles['TableStyled'] : ``
    } ${className}`}
  >
    {children}
  </table>
);

const TableBody: FC<TableBodyProps> = ({ children, className = '' }) => (
  <tbody className={className}>{children}</tbody>
);

const TableHead: FC<TableHeadProps> = ({ children }) => (
  <thead>{children}</thead>
);

const TableRow: FC<TableRowProps> = ({
  className = '',
  children,
  ...props
}) => (
  <tr className={className} {...props}>
    {children}
  </tr>
);

const TableCell = ({
  Element = TABLE_CELL_TYPE_DATA,
  children,
  width = '100%',
  className = '',
  ...other
}: TableCellProps) => (
  <Element
    className={`${styles['TableCell']} ${className}`}
    {...other}
    width={width}
  >
    {children}
  </Element>
);

export { Table, TableBody, TableCell, TableHead, TableRow };
